import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  OwcTypography,
  OwcSelectDropdown,
  OwcEditableDropdown,
  OwcListItem,
  OwcIcon,
  OwcButton,
  OwcProgressBar,
} from '@one/react';
import { useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router-dom';
import { useGroupData } from '../../contexts/GroupDataContext';
import {
  useLazyGetReleaseVersionsForEntityQuery,
  useLazyGetEntitySchemaQuery,
  useLazyGetGroupDataQuery,
} from '../../rtk/api/datagroupsApi';
import { diffJson } from '../../utils/JsonDiff';
import { isLatestOnlyGroup } from '../../utils/getDataGroupPath';
import { normalizeSchemaVersions } from '../../utils/general/schemaVersions';
import {
  findSchemaItem,
  findSchemaItemFromDashboardPath,
} from '../../utils/general/schemaSelection';
import { getDashboardSections } from '../../config/uiConfig';
import styles from './SchemaComparisionPage.module.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

// Preserves today's exact Lab/DiabetesCare labels; everything else falls back
// to the section's own config title (e.g. "Requests", "FHIR IG").
const KNOWN_TYPE_LABELS = {
  event: 'Event',
  resource: 'Resource',
  complexDataType: 'Complex Data Type',
};

function getTypeLabel(section) {
  return KNOWN_TYPE_LABELS[section.type] || section.title;
}

function joinWithOr(list) {
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} or ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, or ${list[list.length - 1]}`;
}

function buildBreadcrumb(selectedItem, sectionByType) {
  if (!selectedItem) return [];
  const crumbs = [];
  // Parentless items (e.g. complex data types) carry an empty `parent`, so
  // this naturally skips subgroup crumbs for them without a type check.
  if (selectedItem.parent) {
    selectedItem.parent.split('/').forEach((sg) => {
      crumbs.push({ label: sg, kind: 'subgroup', tagLabel: 'Sub Group' });
    });
  }
  const section = sectionByType[selectedItem.type];
  const tagLabel = section ? getTypeLabel(section) : selectedItem.type;
  crumbs.push({ label: selectedItem.name, kind: selectedItem.type, tagLabel });
  return crumbs;
}

// ── Breadcrumb sub-component ─────────────────────────────────────────────────

const BREADCRUMB_STYLES = {
  subgroup: { card: styles.breadcrumbSubgroup, icon: 'folder' },
  event: { card: styles.breadcrumbEvent, icon: 'notification' },
  resource: { card: styles.breadcrumbResource, icon: 'data_source' },
  complexDataType: { card: styles.breadcrumbComplex, icon: 'data_table' },
};
const DEFAULT_BREADCRUMB_STYLE = { card: styles.breadcrumbDefault, icon: 'layers' };

function BreadcrumbTrail({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className={styles.breadcrumbContainer}>
      {items.map((crumb, i) => {
        const bs = BREADCRUMB_STYLES[crumb.kind] || DEFAULT_BREADCRUMB_STYLE;
        return (
          <div key={i} className={styles.breadcrumbItem}>
            {i > 0 && (
              <OwcIcon
                name="chevron_right"
                family="outlined"
                className={styles.breadcrumbChevron}
              />
            )}
            <div className={`${styles.breadcrumbCard} ${bs.card}`}>
              <OwcIcon name={bs.icon} family="outlined" className={styles.breadcrumbCardIcon} />
              <div className={styles.breadcrumbCardContent}>
                <span className={styles.breadcrumbCardTag}>{crumb.tagLabel}</span>
                <span className={styles.breadcrumbCardName}>{crumb.label}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VersionOption({ version, unavailable }) {
  return (
    <OwcListItem
      value={version}
      aria-disabled={unavailable}
      title={unavailable ? 'Already selected in the other version dropdown' : undefined}
      className={unavailable ? styles.versionOptionDisabled : undefined}
      onClick={(event) => {
        if (!unavailable) return;
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {version}
    </OwcListItem>
  );
}

// ── Side-by-side diff renderer ───────────────────────────────────────────────

function SideBySideDiff({ diffResult, versionA, versionB }) {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const syncing = useRef(false);

  const lines = useMemo(() => diffResult?.lines || [], [diffResult]);
  const stats = diffResult?.stats || { added: 0, removed: 0, changed: 0 };

  // Group consecutive changed/added/removed lines into navigable blocks.
  const changeBlocks = useMemo(() => {
    const CHANGE_TYPES = new Set(['added', 'removed', 'changed']);
    const blocks = [];
    let start = -1;
    lines.forEach((line, i) => {
      const isChange = CHANGE_TYPES.has(line.type);
      if (isChange && start === -1) {
        start = i;
      } else if (!isChange && start !== -1) {
        blocks.push({ start, end: i - 1 });
        start = -1;
      }
    });
    if (start !== -1) blocks.push({ start, end: lines.length - 1 });
    return blocks;
  }, [lines]);

  const [currentChange, setCurrentChange] = useState(0);

  // Reset navigation when a new diff is rendered.
  useEffect(() => {
    setCurrentChange(0);
  }, [diffResult]);

  const handleScroll = useCallback((source) => {
    if (syncing.current) return;
    syncing.current = true;
    const from = source === 'left' ? leftRef.current : rightRef.current;
    const to = source === 'left' ? rightRef.current : leftRef.current;
    if (from && to) {
      to.scrollTop = from.scrollTop;
    }
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  }, []);

  const scrollToBlock = useCallback(
    (index) => {
      if (index < 0 || index >= changeBlocks.length) return;
      const lineIdx = changeBlocks[index].start;
      // Offset a little so the target isn't flush against the top edge.
      const OFFSET = 40;
      syncing.current = true;
      [leftRef.current, rightRef.current].forEach((pane) => {
        const el = pane?.children[lineIdx];
        if (!pane || !el) return;
        const delta = el.getBoundingClientRect().top - pane.getBoundingClientRect().top;
        pane.scrollTop = pane.scrollTop + delta - OFFSET;
      });
      requestAnimationFrame(() => {
        syncing.current = false;
      });
      setCurrentChange(index);
    },
    [changeBlocks]
  );

  const goPrev = useCallback(() => {
    scrollToBlock(Math.max(0, currentChange - 1));
  }, [currentChange, scrollToBlock]);

  const goNext = useCallback(() => {
    scrollToBlock(Math.min(changeBlocks.length - 1, currentChange + 1));
  }, [currentChange, changeBlocks.length, scrollToBlock]);

  if (!diffResult) return null;

  const activeBlock = changeBlocks[currentChange];
  const isLineActive = (i) => activeBlock && i >= activeBlock.start && i <= activeBlock.end;

  return (
    <div className={styles.diffContainer}>
      {/* Header with stats */}
      <div className={styles.diffHeader}>
        <div className={styles.diffHeaderLeft}>
          <OwcIcon name="compare_arrows" family="outlined" style={{ fontSize: 18 }} />
          <OwcTypography variant="title6">
            Schema Diff: v{versionA} vs v{versionB}
          </OwcTypography>
        </div>
        <div className={styles.diffStats}>
          {stats.added > 0 && (
            <span className={`${styles.diffStatBadge} ${styles.diffStatAdded}`}>
              +{stats.added} added
            </span>
          )}
          {stats.removed > 0 && (
            <span className={`${styles.diffStatBadge} ${styles.diffStatRemoved}`}>
              &minus;{stats.removed} removed
            </span>
          )}
          {stats.changed > 0 && (
            <span className={`${styles.diffStatBadge} ${styles.diffStatChanged}`}>
              ~{stats.changed} changed (old)
            </span>
          )}
          {stats.changed > 0 && (
            <span className={`${styles.diffStatBadge} ${styles.diffStatChangedRight}`}>
              ~{stats.changed} changed (new)
            </span>
          )}
          {stats.added === 0 && stats.removed === 0 && stats.changed === 0 && (
            <span className={`${styles.diffStatBadge} ${styles.diffStatSame}`}>No differences</span>
          )}

          {changeBlocks.length > 0 && (
            <div className={styles.changeNav}>
              <button
                type="button"
                className={styles.changeNavBtn}
                onClick={goPrev}
                disabled={currentChange <= 0}
                title="Previous change"
                aria-label="Previous change"
              >
                <OwcIcon name="chevron_up" family="outlined" style={{ fontSize: 16 }} />
              </button>
              <span className={styles.changeNavCount}>
                {currentChange + 1} / {changeBlocks.length}
              </span>
              <button
                type="button"
                className={styles.changeNavBtn}
                onClick={goNext}
                disabled={currentChange >= changeBlocks.length - 1}
                title="Next change"
                aria-label="Next change"
              >
                <OwcIcon name="chevron_down" family="outlined" style={{ fontSize: 16 }} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Column headers */}
      <div className={styles.splitColumnHeaders}>
        <div className={styles.splitColumnHeader}>
          <OwcIcon name="history" family="outlined" style={{ fontSize: 14 }} />
          <span>v{versionA}</span>
        </div>
        <div className={styles.splitColumnHeader}>
          <OwcIcon name="update" family="outlined" style={{ fontSize: 14 }} />
          <span>v{versionB}</span>
        </div>
      </div>

      {/* Side-by-side panes */}
      <div className={styles.sideBySideBody}>
        {/* Left pane */}
        <div className={styles.diffPane} ref={leftRef} onScroll={() => handleScroll('left')}>
          {lines.map((line, i) => {
            let cls = styles.diffLineNormal;
            if (line.type === 'removed') cls = styles.diffLineRemoved;
            else if (line.type === 'changed') cls = styles.diffLineChanged;
            const active = isLineActive(i) ? ` ${styles.diffLineActive}` : '';

            return (
              <div key={i} className={`${styles.diffLine} ${cls}${active}`}>
                <span className={styles.diffLineNum}>{line.leftNum || ''}</span>
                <span className={styles.diffLinePrefix}>
                  {line.type === 'removed'
                    ? '\u2212'
                    : line.type === 'changed'
                      ? '~'
                      : line.type === 'added'
                        ? ''
                        : ' '}
                </span>
                <span className={styles.diffLineText}>{line.left ?? ''}</span>
              </div>
            );
          })}
        </div>

        {/* Right pane */}
        <div className={styles.diffPane} ref={rightRef} onScroll={() => handleScroll('right')}>
          {lines.map((line, i) => {
            let cls = styles.diffLineNormal;
            if (line.type === 'added') cls = styles.diffLineAdded;
            else if (line.type === 'changed') cls = styles.diffLineChangedRight;
            const active = isLineActive(i) ? ` ${styles.diffLineActive}` : '';

            return (
              <div key={i} className={`${styles.diffLine} ${cls}${active}`}>
                <span className={styles.diffLineNum}>{line.rightNum || ''}</span>
                <span className={styles.diffLinePrefix}>
                  {line.type === 'added'
                    ? '+'
                    : line.type === 'changed'
                      ? '~'
                      : line.type === 'removed'
                        ? ''
                        : ' '}
                </span>
                <span className={styles.diffLineText}>{line.right ?? ''}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function SchemaComparisonPage() {
  const auth = useAuth();
  const location = useLocation();
  const { groupData, lastDashboardPath } = useGroupData();
  const groupName = groupData?.groupName || 'Lab';
  // Latest-only groups (e.g. DiabetesCare) compare entity schema versions
  // directly. Full-featured groups (e.g. Lab) compare release versions and
  // resolve them to schema versions before fetching.
  const latestOnly = isLatestOnlyGroup(groupName);

  // Sidebar sections for the active group — the single source of truth for
  // which entity types this page should offer, mirroring OwcDrawer.jsx.
  const sections = useMemo(() => getDashboardSections(groupName), [groupName]);
  const sectionByType = useMemo(
    () => Object.fromEntries(sections.map((s) => [s.type, s])),
    [sections]
  );
  const typeLabels = useMemo(() => sections.map(getTypeLabel), [sections]);
  const flatTypes = useMemo(
    () => new Set(sections.filter((s) => s.needsSchemaSetters).map((s) => s.type)),
    [sections]
  );

  const [triggerGetReleaseVersions] = useLazyGetReleaseVersionsForEntityQuery();
  const [triggerGetEntitySchema] = useLazyGetEntitySchemaQuery();
  const [triggerGetGroupData] = useLazyGetGroupDataQuery();

  // Build selectable items from current group data, driven by the active
  // group's sidebar sections instead of a hardcoded event/resource/CDT list.
  const selectableItems = useMemo(() => {
    const items = [];
    sections.forEach((section) => {
      const list = groupData?.[section.itemsSource];
      if (!Array.isArray(list)) return;
      const typeLabel = getTypeLabel(section);
      list.forEach((entry) => {
        if (!entry?.name) return;
        if (!section.needsSchemaSetters && !entry.parent) return;
        items.push({
          type: section.type,
          name: entry.name,
          parent: entry.parent || '',
          version: entry.version,
          label: `${typeLabel}: ${entry.name}`,
        });
      });
    });
    return items;
  }, [groupData, sections]);

  // Auto-detect item from last dashboard URL
  const detectedItem = useMemo(
    () =>
      findSchemaItem(selectableItems, location.state?.schemaSelection) ||
      findSchemaItemFromDashboardPath(selectableItems, lastDashboardPath, flatTypes),
    [lastDashboardPath, location.state?.schemaSelection, selectableItems, flatTypes]
  );

  const [selectedItemKey, setSelectedItemKey] = useState('');
  const selectedItem = useMemo(() => {
    if (!selectedItemKey) return detectedItem;
    return (
      selectableItems.find(
        (item) => `${item.type}-${item.parent}/${item.name}` === selectedItemKey
      ) || null
    );
  }, [selectedItemKey, selectableItems, detectedItem]);

  useEffect(() => {
    if (detectedItem && !selectedItemKey) {
      setSelectedItemKey(`${detectedItem.type}-${detectedItem.parent}/${detectedItem.name}`);
    }
  }, [detectedItem, selectedItemKey]);

  const breadcrumb = useMemo(
    () => buildBreadcrumb(selectedItem, sectionByType),
    [selectedItem, sectionByType]
  );

  // Release versions for the selected entity
  const [releaseVersions, setReleaseVersions] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [releasesError, setReleasesError] = useState(null);

  // Version selection
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');

  // Diff state
  const [diffResult, setDiffResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [compareError, setCompareError] = useState(null);

  // Fetch release versions when selected item changes
  useEffect(() => {
    async function fetchReleaseVersions() {
      if (!auth?.user?.id_token || !selectedItem) {
        setReleaseVersions([]);
        return;
      }
      try {
        setReleasesLoading(true);
        setReleasesError(null);
        setReleaseVersions([]);
        setVersionA('');
        setVersionB('');
        setDiffResult(null);
        setCompareError(null);

        const { data: versions, error } = await triggerGetReleaseVersions(
          {
            groupName,
            type: selectedItem.type,
            parent: selectedItem.parent,
            name: selectedItem.name,
            list: latestOnly ? 'version' : 'releaseVersion',
          },
          false
        );

        if (error)
          throw new Error(
            error.message || `Failed to fetch ${latestOnly ? 'versions' : 'release versions'}`
          );

        const normalizedVersions = normalizeSchemaVersions(versions);
        setReleaseVersions(normalizedVersions);
        if (normalizedVersions.length >= 2) {
          setVersionA(normalizedVersions[1]);
          setVersionB(normalizedVersions[0]);
        } else if (normalizedVersions.length === 1) {
          setVersionA(normalizedVersions[0]);
        }
      } catch (err) {
        console.error('Failed to fetch release versions:', err);
        setReleasesError(err.message);
      } finally {
        setReleasesLoading(false);
      }
    }
    fetchReleaseVersions();
  }, [auth?.user?.id_token, selectedItem, groupName, latestOnly]);

  /**
   * Resolve the entity's schema version for a given release version by
   * fetching the group data for that release and finding the matching entity.
   */
  const resolveEntitySchemaVersion = useCallback(
    async (releaseVersion, item) => {
      const { data, error } = await triggerGetGroupData(
        {
          groupName,
          releaseVersion,
          latest: false,
        },
        false
      );

      if (error)
        throw new Error(
          error.message || `Failed to fetch group data for release ${releaseVersion}`
        );

      const itemsSource = sectionByType[item.type]?.itemsSource;
      const list = itemsSource ? data?.[itemsSource] : undefined;
      if (!Array.isArray(list))
        throw new Error(`No "${item.type}" items found in release ${releaseVersion}`);

      const entity =
        list.find((e) => e.name === item.name && e.parent === item.parent) ||
        list.find((e) => e.name === item.name);

      if (!entity || !entity.version) {
        throw new Error(`${item.name} not found in release ${releaseVersion}`);
      }

      return entity.version;
    },
    [groupName, triggerGetGroupData, sectionByType]
  );

  // Compare: resolve entity schema versions, fetch both schemas, diff on frontend
  const handleCompare = useCallback(async () => {
    if (!versionA || !versionB || !selectedItem) return;

    setComparing(true);
    setCompareError(null);
    setDiffResult(null);

    try {
      // Latest-only groups list entity schema versions directly, so the
      // selected values are already schema versions. Full-featured groups list
      // release versions which must first be resolved to schema versions.
      const [schemaVersionA, schemaVersionB] = latestOnly
        ? [versionA, versionB]
        : await Promise.all([
            resolveEntitySchemaVersion(versionA, selectedItem),
            resolveEntitySchemaVersion(versionB, selectedItem),
          ]);

      // Fetch both schemas in parallel
      const [resultA, resultB] = await Promise.all([
        triggerGetEntitySchema(
          {
            groupName,
            type: selectedItem.type,
            parent: selectedItem.parent,
            name: selectedItem.name,
            version: schemaVersionA,
          },
          false
        ),
        triggerGetEntitySchema(
          {
            groupName,
            type: selectedItem.type,
            parent: selectedItem.parent,
            name: selectedItem.name,
            version: schemaVersionB,
          },
          false
        ),
      ]);

      if (resultA.error)
        throw new Error(resultA.error.message || `Failed to fetch schema v${versionA}`);
      if (resultB.error)
        throw new Error(resultB.error.message || `Failed to fetch schema v${versionB}`);

      // Diff on frontend
      const diff = diffJson(resultA.data, resultB.data);
      setDiffResult(diff);
    } catch (err) {
      console.error('Schema compare error:', err);
      setCompareError(err.message);
    } finally {
      setComparing(false);
    }
  }, [
    versionA,
    versionB,
    selectedItem,
    groupName,
    latestOnly,
    triggerGetEntitySchema,
    resolveEntitySchemaVersion,
  ]);

  const canCompare = versionA && versionB && versionA !== versionB && selectedItem && !comparing;

  return (
    <div className={styles.container}>
      <OwcTypography variant="title4">Schema Comparison</OwcTypography>
      <OwcTypography className={styles.subtitle}>
        Select any {joinWithOr(typeLabels)}, pick two versions, and compare schemas side
        by side.
      </OwcTypography>

      <BreadcrumbTrail items={breadcrumb} />

      {/* Controls bar */}
      <div className={styles.bottomBar}>
        <div className={styles.dropdownGroup}>
          <span className={styles.dropdownLabel}>Schema</span>
          <OwcEditableDropdown
            label={typeLabels.join(' / ')}
            size="medium"
            placeholder={`Search ${joinWithOr(typeLabels.map((l) => l.toLowerCase()))}`}
            validity={{ state: 'valid' }}
            value={selectedItemKey}
            onValueChange={(e) => {
              const val = Array.isArray(e.detail) ? e.detail[0] : e.detail;
              setSelectedItemKey(val || '');
            }}
            style={{ flex: 1, width: 300 }}
            no-clear-icon
            disabled={selectableItems.length === 0}
          >
            {selectableItems.map((item) => {
              const key = `${item.type}-${item.parent}/${item.name}`;
              return (
                <OwcListItem key={key} value={key}>
                  {item.label}
                </OwcListItem>
              );
            })}
          </OwcEditableDropdown>
        </div>

        <div className={styles.dropdownGroup}>
          <span className={styles.dropdownLabel}>Version A</span>
          <OwcSelectDropdown
            label="Version A"
            variant="outlined"
            size="medium"
            placeholder="Select version"
            value={versionA}
            onValueChange={(e) => {
              const val = Array.isArray(e.detail) ? e.detail[0] : e.detail;
              if (!val || val === versionB) {
                e.currentTarget.value = versionA;
                return;
              }
              setVersionA(val);
            }}
            style={{ flex: 1, maxWidth: 220 }}
            noClearIcon="true"
            disabled={releasesLoading || releaseVersions.length === 0}
          >
            {releaseVersions.map((v) => (
              <VersionOption key={v} version={v} unavailable={v === versionB} />
            ))}
          </OwcSelectDropdown>
        </div>

        <div className={styles.vsLabel}>VS</div>

        <div className={styles.dropdownGroup}>
          <span className={styles.dropdownLabel}>Version B</span>
          <OwcSelectDropdown
            label="Version B"
            size="medium"
            variant="outlined"
            placeholder="Select version"
            value={versionB}
            onValueChange={(e) => {
              const val = Array.isArray(e.detail) ? e.detail[0] : e.detail;
              if (!val || val === versionA) {
                e.currentTarget.value = versionB;
                return;
              }
              setVersionB(val);
            }}
            style={{ flex: 1, maxWidth: 220 }}
            noClearIcon="true"
            disabled={releasesLoading || releaseVersions.length === 0}
          >
            {releaseVersions.map((v) => (
              <VersionOption key={v} version={v} unavailable={v === versionA} />
            ))}
          </OwcSelectDropdown>
        </div>

        <OwcButton
          variant="primary"
          disabled={!canCompare}
          onClick={handleCompare}
          className={styles.compareButton}
        >
          <OwcIcon name="compare_arrows" family="outlined" slot="prefix" />
          Compare
        </OwcButton>
      </div>

      {/* Loading / error states */}
      {releasesLoading && (
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
          <OwcProgressBar
            caption={latestOnly ? 'Loading versions...' : 'Loading release versions...'}
            value={undefined}
            size="small"
            style={{ width: '50%' }}
          />
        </div>
      )}
      {releasesError && (
        <div className={styles.compareError} style={{ marginTop: 12 }}>
          <OwcIcon name="warning" family="outlined" style={{ fontSize: 16, marginRight: 6 }} />
          {releasesError}
        </div>
      )}
      {comparing && (
        <div className={styles.diffContainer}>
          <div className={styles.emptyState}>
            <OwcProgressBar
              caption="Fetching and comparing schemas..."
              value={undefined}
              size="small"
              style={{ width: '60%' }}
            />
          </div>
        </div>
      )}

      {/* Diff result */}
      {!comparing && diffResult && (
        <SideBySideDiff diffResult={diffResult} versionA={versionA} versionB={versionB} />
      )}

      {compareError && (
        <div className={styles.compareError}>
          <OwcIcon name="warning" family="outlined" style={{ fontSize: 16, marginRight: 6 }} />
          {compareError}
        </div>
      )}

      {!selectedItem && selectableItems.length === 0 && !releasesLoading && (
        <div className={styles.compareError} style={{ marginTop: 12 }}>
          <OwcIcon name="info" family="outlined" style={{ fontSize: 16, marginRight: 6 }} />
          Select a schema item ({joinWithOr(typeLabels)}) from the Explorer sidebar first, then
          navigate here.
        </div>
      )}
    </div>
  );
}
